import { Fragment, useEffect, useState } from "react";
import { Button } from "./Button";
import { PutAwaySection } from "./PutAwaySection";
import { optionsApi } from "../../services/optionsApi";

/**
 * Put a value away, or bring it back (#417).
 *
 * ── THE ENDPOINT EXISTED AND NOTHING CALLED IT ──
 *
 * `DELETE /options/{list_key}/{option_id}` has been complete, correct and
 * tested since the option lists were written. `optionsApi.retire` was
 * even wired up here -- and had no callers on either platform. So every
 * generic option list in the app could be added to and never pruned.
 *
 * That is the orphan pattern with the wires the other way round: usually
 * a file nothing imports, here a live write path with no route into it.
 *
 * ── PUT AWAY, NEVER DELETE ──
 *
 * The same rule VarietalManager states beside it: a button labelled
 * Delete that leaves the data in place teaches people the app is lying
 * somewhere, and they are then right to distrust the rest of it.
 *
 * The reason differs from varietals, and it is worth knowing. Honey keys
 * on varietal_id, so that row must survive or last season's Clover
 * renders blank. A generic option's consumers store the TEXT --
 * `EquipmentInventory.item_type` is an NVARCHAR, not a reference -- so
 * retiring one cannot break history at all. It stays soft anyway, because
 * that is what makes it reversible.
 *
 * ── RENAME IS A MERGE, WHICH IS WHY IT EXISTS NOW ──
 *
 * This said there was no rename, and the reason was right: consumers
 * store the TEXT, so renaming "Excluder" would leave every row already
 * filed under the old spelling naming a value that no longer exists.
 *
 * That reason is also the specification. Rewriting the stored text and
 * retiring the old value IS a merge -- so a rename is a merge into a
 * value that does not exist yet. One endpoint, two labels, and the
 * orphan cannot happen because moving the records is the operation
 * rather than a step somebody might forget.
 *
 * Both are offered here because they read differently at the point of
 * use: "Frames should have been Frame" is a rename, "we have both and
 * want one" is a merge. Same call underneath.
 *
 * ── BUILT-INS CANNOT BE PUT AWAY ──
 *
 * They are not rows -- the server sends them with a null option_id -- so
 * there is nothing to deactivate. Listed without the control rather than
 * hidden: a list that silently omits "Frame" while the picker offers it
 * is a list somebody reads as broken.
 *
 * MIRRORS mobile's OptionListManager, rule for rule.
 */
export function OptionListManager({ listKey, onChanged, emptyText }) {
  const [options, setOptions] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  /* The row being renamed or merged, and the typed target. One at a time
     deliberately: two open editors on one list is how somebody renames
     the wrong row. */
  const [editing, setEditing] = useState(null);
  const [target, setTarget] = useState("");
  const [note, setNote] = useState(null);

  async function load() {
    try {
      const r = await optionsApi.listForManaging(listKey);
      setOptions(r.options || []);
    } catch {
      // null, not []: "we could not check" is not "you have none", and
      // this is housekeeping beside the thing somebody came for.
      setOptions(null);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listKey]);

  if (options === null) return null;

  const mine = options.filter((o) => o.option_id != null);
  const builtIn = options.filter((o) => o.option_id == null);

  async function setActive(option, active) {
    setBusy(option.option_id);
    setError(null);
    try {
      if (active) await optionsApi.restore(listKey, option.option_id);
      else await optionsApi.retire(listKey, option.option_id);
      await load();
      // The pickers on this page are holding the old list. Without this a
      // value just put away stays selectable until the next refresh.
      onChanged?.();
    } catch (err) {
      setError(err.apiMessage || "Could not change that");
    } finally {
      setBusy(null);
    }
  }

  async function applyMerge(option) {
    const into = target.trim();
    if (!into) return setError("Type the name to use.");
    setBusy(option.option_id);
    setError(null);
    setNote(null);
    try {
      const r = await optionsApi.merge(listKey, option.option_id, into);
      /* SAID OUT LOUD, including zero. "Renamed Frames to Frame" that
         moved no records means the duplicate was never used -- which is
         a different situation from one that was, and the keeper is the
         only person who can tell whether that is a surprise. */
      setNote(
        r.records_repointed > 0
          ? `${r.merged} is now ${r.into} — ${r.records_repointed} ` +
            `record${r.records_repointed === 1 ? "" : "s"} moved across.`
          : `${r.merged} is now ${r.into}. Nothing was filed under it, so no records moved.`
      );
      setEditing(null);
      setTarget("");
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.apiMessage || "Could not merge that");
    } finally {
      setBusy(null);
    }
  }

  /* THE ROW, LIFTED OUT OF THE MAP (#486).

     Put-away values now render in a second, collapsed list, so the
     same markup is needed in two places. Lifted rather than copied:
     two copies of a row carrying a rename editor and a put-away
     toggle would be two places to fix the next time either changes.

     Still a function rather than a component, so `editing`, `busy`
     and `setActive` stay in scope exactly as they were. */
  const renderRow = (o) => (
          /* A FRAGMENT because each value renders two rows when its
             editor is open -- the value itself, and the field below
             it. Nesting the editor inside the first <li> would put a
             text input inside a flex row built for a label and two
             buttons, and it would squeeze on a narrow window. */
          <Fragment key={o.option_id}>
          <li style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: "var(--space-2)", padding: "var(--space-2) 0",
            borderBottom: "1px solid var(--color-border)",
          }}>
            <span style={{ color: o.is_active ? "var(--color-text)" : "var(--color-text-muted)" }}>
              {o.value}
              {/* Said on the row rather than by greying it alone: a
                  muted row is a state people miss. */}
              {o.is_active ? "" : " · put away"}
            </span>
            <span style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
              {/* Only on values still in use. Renaming something already
                  put away moves records onto a value nobody is offered,
                  which is the orphan this feature exists to avoid. */}
              {o.is_active ? (
                <Button
                  variant="secondary"
                  disabled={busy === o.option_id}
                  onClick={() => {
                    setEditing(editing === o.option_id ? null : o.option_id);
                    setTarget("");
                    setError(null);
                  }}
                >
                  {editing === o.option_id ? "Cancel" : "Rename or merge"}
                </Button>
              ) : null}
              <Button
                variant="secondary"
                disabled={busy === o.option_id}
                onClick={() => setActive(o, !o.is_active)}
              >
                {o.is_active ? "Put away" : "Bring back"}
              </Button>
            </span>
          </li>
          {editing === o.option_id ? (
            <li style={{ padding: "var(--space-2) 0" }}>
              <label style={{ display: "block", fontSize: "var(--font-size-sm)",
                              color: "var(--color-text-muted)" }}>
                {/* ONE FIELD FOR BOTH, because the operation is the
                    same. Typing a name that already exists merges into
                    it; typing a new one renames. Said in the hint
                    rather than split into two controls that would do
                    identical things. */}
                {/*
                  * A DATALIST, SO THE EXISTING NAMES ARE OFFERED (#500).
                  *
                  * Asked 6 Oct: "it might be better if the Rename box was
                  * a drop down so the user could enter a new name or
                  * choose an existing name."
                  *
                  * Right, and it matches what this control does. Rename
                  * and merge are ONE operation here (see the comment at
                  * the top of this file: retiring the old value while
                  * rewriting its records IS a merge), so the question is
                  * "which name should these records end up under" -- and
                  * the existing names are the likeliest answers.
                  *
                  * MIS-TYPING WAS THE REAL COST. The merge matches on the
                  * VALUE, so "Fame" instead of "Frame" silently created a
                  * third value rather than folding into the second -- the
                  * orphan this feature exists to prevent, reached through
                  * the feature itself.
                  *
                  * A datalist rather than a <select>, because a free name
                  * has to stay typeable: renaming to something that does
                  * not exist yet is half of what this control is for, and
                  * a select would make the rename case impossible.
                  */}
                Use this name instead — an existing name merges the two
                <input id="option-list-manager-target" name="target"
                  list="option-list-manager-names"
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  className="touch-target"
                  style={{ display: "block", marginTop: 4, width: "100%", maxWidth: 320 }}
                  placeholder={`e.g. ${builtIn[0]?.value || "Frame"}`}
                />
              </label>
              {/* Every name on this list EXCEPT the row being edited --
                  merging a value into itself is the one answer that
                  cannot work, and offering it would invite it. Built-ins
                  are included: folding a custom value into "Frame" is
                  the commonest tidy-up there is. */}
              <datalist id="option-list-manager-names">
                {[...builtIn, ...mine]
                  .filter((other) => other.option_id !== o.option_id)
                  .map((other) => (
                    <option key={`${other.option_id ?? "builtin"}-${other.value}`}
                            value={other.value} />
                  ))}
              </datalist>
              <Button
                disabled={busy === o.option_id || !target.trim()}
                onClick={() => applyMerge(o)}
                style={{ marginTop: "var(--space-2)" }}
              >
                {busy === o.option_id ? "Moving records…" : `Replace ${o.value}`}
              </Button>
            </li>
          ) : null}
          </Fragment>
  );

  return (
    <div>
      {note ? (
        <p style={{ color: "var(--color-text-muted)", marginTop: 0 }}>{note}</p>
      ) : null}
      {error ? (
        <p style={{ color: "var(--color-danger)", marginTop: 0 }}>{error}</p>
      ) : null}

      {mine.length === 0 ? (
        <p style={{ color: "var(--color-text-muted)", marginTop: 0 }}>
          {emptyText || "Nothing to tidy here yet."}
        </p>
      ) : (
        /*
         * WHAT YOU USE FIRST, WHAT YOU PUT AWAY BEHIND A DISCLOSURE
         * (#486). Put-away values used to render inline and
         * interleaved, so a tidied list read as mostly things the
         * account had stopped using.
         *
         * `o.is_active` is truthy here -- this endpoint always sends
         * the column -- which is NOT how BottleSizeManager reads it,
         * hence splitPutAway taking the test as an argument.
         */
        <PutAwaySection items={mine} isActive={(o) => Boolean(o.is_active)}>
          {({ active, putAway, open, disclosure }) => (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {active.map(renderRow)}
              {disclosure ? <li>{disclosure}</li> : null}
              {open ? putAway.map(renderRow) : null}
            </ul>
          )}
        </PutAwaySection>
      )}

      {builtIn.length > 0 ? (
        <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)", marginBottom: 0 }}>
          {/* Named, so the absence of a control on them is an explanation
              rather than a gap. */}
          Always available: {builtIn.map((o) => o.value).join(", ")}
        </p>
      ) : null}
    </div>
  );
}
